/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { autorun } from 'mobx';
import * as React from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { UtterancePredictionsStore } from 'src/luis/projects/utterances/hooks/useUtterances';
import { Utterance } from 'src/luis/projects/utterances/types/Utterance';
import { utteranceDenormalizer } from 'src/luis/projects/utterances/utils/utteranceDenormalizer';

/**
 * Sets up an autorun function that subscribes to changes in entities, intents, the given utterance
 * and prediction. Whenever a change happens, it generates a new utterance object and sets it to the
 * utterance state returned to the caller.
 *
 * This function uses autorun to remove de-normalization from the rendering path and avoid generating new
 * objects every time react re-renders.
 */
export const useUtterance = (utterancePrediction: UtterancePredictionsStore): Utterance => {
    const { rootStore } = useGlobalState();
    const currentAppStore = rootStore.currentLuisAppStore;

    const { entityStoreHouse, intentStoreHouse, phraseListStoreHouse } = currentAppStore.currentAppVersionStore;

    const [utterance, setUtterance] = React.useState<Utterance>(() =>
        utteranceDenormalizer({
            utterancePrediction,
            intentStores: intentStoreHouse.items,
            entityStores: entityStoreHouse.items,
            phraseListStores: phraseListStoreHouse.items
        })
    );

    React.useEffect(
        () =>
            autorun(() =>
                setUtterance(
                    utteranceDenormalizer({
                        utterancePrediction,
                        intentStores: intentStoreHouse.items,
                        entityStores: entityStoreHouse.items,
                        phraseListStores: phraseListStoreHouse.items
                    })
                )
            ),
        [utterancePrediction.predictionStore, utterancePrediction.utteranceStore]
    );

    return utterance;
};
